/**
 * V10 Phase C — 현재 edge 의 episodes 안에서 의미 검색.
 *
 * 전체 서비스에 대한 `edge_semantic_search` 결과 중 현재 edge 와 일치하는 hit 만 필터링해
 * 로컬 하이라이트에 쓴다 (MCP 툴은 전체 edges 를 대상으로 검색하므로 클라이언트 필터).
 */
import { useState } from "react"
import type { AdminClient, SemanticSearchHit } from "../../../api/admin-client.ts"

type Props = {
	readonly client: AdminClient
	readonly slug: string
	readonly edgeKey: string
	readonly onSelectEpisode?: (episodeId: string) => void
}

export function SemanticSearchWithinEdge(props: Props) {
	const [query, setQuery] = useState("")
	const [hits, setHits] = useState<ReadonlyArray<SemanticSearchHit>>([])
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | undefined>()

	async function onSearch() {
		if (!query.trim()) return
		setBusy(true)
		setError(undefined)
		try {
			const all = await props.client.semanticSearchEpisodes(props.slug, {
				query: query.trim(),
				topK: 20
			})
			setHits(all.filter((h) => h.edgeKey === props.edgeKey))
		} catch (e) {
			setError((e as Error).message)
		} finally {
			setBusy(false)
		}
	}

	return (
		<div style={{ marginTop: "var(--pk-space-4)" }}>
			<h4 style={{ margin: 0, marginBottom: "var(--pk-space-2)", fontSize: "0.9rem" }}>
				이 edge 안에서 검색
			</h4>
			<div style={{ display: "flex", gap: "var(--pk-space-2)" }}>
				<input
					className="input"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter" && !busy) void onSearch()
					}}
					placeholder="의미 검색 (content)"
					style={{ flex: 1 }}
				/>
				<button
					type="button"
					className="btn btn-primary btn-sm"
					onClick={onSearch}
					disabled={busy || !query.trim()}
				>
					{busy ? "..." : "검색"}
				</button>
			</div>
			{error && (
				<div className="alert alert-danger" style={{ marginTop: "var(--pk-space-2)", fontSize: "0.8rem" }}>
					{error}
				</div>
			)}
			{hits.length > 0 && (
				<div style={{ marginTop: "var(--pk-space-2)" }}>
					{hits.map((h) => (
						<div
							key={h.edgeKey}
							style={{
								padding: "var(--pk-space-2)",
								background: "var(--pk-accent-subtle)",
								borderRadius: "var(--pk-radius-sm)",
								marginBottom: 4,
								fontSize: "0.8rem",
								cursor: props.onSelectEpisode ? "pointer" : "default"
							}}
							onClick={() => props.onSelectEpisode?.(h.topEpisode.episodeId)}
							onKeyDown={(e) => {
								if ((e.key === "Enter" || e.key === " ") && props.onSelectEpisode) {
									props.onSelectEpisode(h.topEpisode.episodeId)
								}
							}}
							role={props.onSelectEpisode ? "button" : undefined}
							tabIndex={props.onSelectEpisode ? 0 : undefined}
						>
							<div style={{ color: "var(--pk-text-muted)", fontSize: "0.7rem", marginBottom: 2 }}>
								strength {h.strength.toFixed(2)} · {h.episodeCount} episodes
							</div>
							<div>{h.topEpisode.content.slice(0, 120)}</div>
						</div>
					))}
				</div>
			)}
			{!busy && hits.length === 0 && query.trim().length > 0 && !error && (
				<div className="muted" style={{ marginTop: "var(--pk-space-2)", fontSize: "0.8rem" }}>
					이 edge 에선 일치 결과 없음
				</div>
			)}
		</div>
	)
}
